import type { ApiQuery, ApiResponse, MutationResult } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { WithdrawalControlsRepository } from './repository';
import type { WithdrawalPolicyListQuery, WithdrawalVelocityListQuery } from './types';

type PolicyListResponse = ApiResponse<'/api/v1/partners/{partnerId}/withdrawal-policies', 'get'>;
type PolicyResponse = ApiResponse<
  '/api/v1/partners/{partnerId}/withdrawal-policies/{policyId}',
  'get'
>;
type VelocityListResponse = ApiResponse<
  '/api/v1/partners/{partnerId}/withdrawal-velocity-rules',
  'get'
>;
type VelocityResponse = ApiResponse<
  '/api/v1/partners/{partnerId}/withdrawal-velocity-rules/{ruleId}',
  'get'
>;

export function createRealWithdrawalControlsRepository(
  apiClient: ApiClient,
  accessToken: string | null,
): WithdrawalControlsRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async listPolicies(partnerId, query, signal) {
      const result = await apiClient.get<PolicyListResponse>(policyCollectionPath(partnerId), {
        ...auth,
        query: policyQuery(query),
        signal,
      });
      return page(result);
    },
    async createPolicy(partnerId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/partners/{partnerId}/withdrawal-policies', 'post'>
      >(policyCollectionPath(partnerId), input, auth);
      return mutation(result);
    },
    async getPolicy(partnerId, policyId, signal) {
      const result = await apiClient.get<PolicyResponse>(policyPath(partnerId, policyId), {
        ...auth,
        signal,
      });
      return result.data.data;
    },
    async updatePolicy(partnerId, policyId, input) {
      const result = await apiClient.patch<
        ApiResponse<'/api/v1/partners/{partnerId}/withdrawal-policies/{policyId}', 'patch'>
      >(policyPath(partnerId, policyId), input, auth);
      return mutation(result);
    },
    async changePolicyStatus(partnerId, policyId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/partners/{partnerId}/withdrawal-policies/{policyId}/status', 'post'>
      >(`${policyPath(partnerId, policyId)}/status`, input, auth);
      return mutation(result);
    },
    async listVelocityRules(partnerId, query, signal) {
      const result = await apiClient.get<VelocityListResponse>(velocityCollectionPath(partnerId), {
        ...auth,
        query: velocityQuery(query),
        signal,
      });
      return page(result);
    },
    async getVelocityRule(partnerId, ruleId, signal) {
      const result = await apiClient.get<VelocityResponse>(velocityPath(partnerId, ruleId), {
        ...auth,
        signal,
      });
      return result.data.data;
    },
  };
}

function policyQuery(
  query: WithdrawalPolicyListQuery,
): ApiQuery<'/api/v1/partners/{partnerId}/withdrawal-policies', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    ...(query.filters.status === '' ? {} : { status: query.filters.status }),
    ...(query.filters.currencyCode.trim() === ''
      ? {}
      : { currencyCode: query.filters.currencyCode.trim().toUpperCase() }),
  };
}

function velocityQuery(
  query: WithdrawalVelocityListQuery,
): ApiQuery<'/api/v1/partners/{partnerId}/withdrawal-velocity-rules', 'get'> {
  return {
    ...policyQuery(query),
    ...(query.filters.dimension === '' ? {} : { dimension: query.filters.dimension }),
  };
}

function page<Item>(result: {
  readonly data: {
    readonly data: readonly Item[];
    readonly pagination: { readonly total: number };
  };
  readonly requestId: string | null;
}) {
  return {
    items: result.data.data,
    total: result.data.pagination.total,
    requestId: result.requestId,
  };
}

function mutation<T>(result: {
  readonly data: { readonly data: T };
  readonly requestId: string | null;
}): MutationResult<T> {
  return { data: result.data.data, requestId: result.requestId };
}

function policyCollectionPath(partnerId: string) {
  return `/api/v1/partners/${encodeURIComponent(partnerId)}/withdrawal-policies`;
}

function policyPath(partnerId: string, policyId: string) {
  return `${policyCollectionPath(partnerId)}/${encodeURIComponent(policyId)}`;
}

function velocityCollectionPath(partnerId: string) {
  return `/api/v1/partners/${encodeURIComponent(partnerId)}/withdrawal-velocity-rules`;
}

function velocityPath(partnerId: string, ruleId: string) {
  return `${velocityCollectionPath(partnerId)}/${encodeURIComponent(ruleId)}`;
}
